import { logWarning } from '@core/logging';
import { openBrowserChannel } from './channels';
import { SessionClassifier, type PairSample, type ResolvedProbe } from './classifier';
import { decodeEcho, decodeLoadReport, encodeProbe } from './frame';
import { LoadSender } from './load';
import { Pacer } from './pacer';
import { createWorkerTicker, type Ticker, type TickerFactory } from './ticker';
import { tokenClaims } from './token';
import type { Channel, ChannelFactory, Clock, LoadLevel, LoadSample, LoadStatus, SessionGrant, TargetStatus, Transport, WindowSummary } from './types';
import { WindowAggregator } from './windows';

export const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10000];
export const NO_ECHO_MS = 10_000;
export const RETRY_MS = 60_000;
export const RENEW_AFTER_MS = 90 * 60_000;
/** Spec 6: UDP trials from a working TCP session, backing off while they keep failing. */
export const UDP_RETRY_MS = [60_000, 120_000, 300_000, 600_000];
/** A session echoing this long resets the reconnect backoff. */
const STABLE_MS = 30_000;
/** Non-echoing reconnects an established UDP target tries before it falls back to TCP. */
const SILENT_UDP_RECONNECTS = 3;
/** A gap between a target's ticks longer than this means the tab was frozen. */
const FROZEN_MS = 1000;
/** A gap between two runs of a target's tick or echo handler longer than this is a main-thread stall (spec 4.3). */
export const STALL_GAP_MS = 100;
/** Server refusals that a quick retry cannot fix (spec 5 limits, 11 token). */
const WAIT_REASONS = new Set(['banned', 'full', 'ip_limit', 'sid_in_use', 'token', 'kind', 'load_budget']);

export interface RunnerOptions {
    intervalMs: number;
    getGrant: () => Promise<SessionGrant>;
    onSummary: (summary: WindowSummary) => void;
    onStatus?: (target: string, status: TargetStatus) => void;
    onLoadStatus?: (status: LoadStatus) => void;
    /** Every probe a target resolves, with its jitter pairs: the same data the windows get, for per-second charts. */
    onResolved?: (target: string, probes: ResolvedProbe[], pairs: PairSample[]) => void;
    /** Every load report of the server, with the bytes sent since the previous report. */
    onLoadSample?: (sample: LoadSample) => void;
    /** A main-thread stall seen by a target: `[fromMono, toMono]` on the probes' clock. Its probes are not judged. */
    onStalled?: (target: string, fromMono: number, toMono: number) => void;
    clock?: Clock;
    openChannel?: ChannelFactory;
    createTicker?: TickerFactory;
    /** Whether the page is in a background tab. It only feeds `backgroundMs`: a hidden page measures like a visible one. */
    isHidden?: () => boolean;
    warn?: (message: string, parameters?: object) => void;
}

const browserClock: Clock = {
    now: () => performance.now(),
    wall: () => Date.now(),
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>)
};

interface Context {
    clock: Clock;
    openChannel: ChannelFactory;
    intervalMs: number;
    token(target: string): string;
    requestRenew(): void;
    hidden(): boolean;
    status(target: string, status: TargetStatus): void;
    summary(summary: WindowSummary): void;
    resolved(target: string, probes: ResolvedProbe[], pairs: PairSample[]): void;
    stalled(target: string, fromMono: number, toMono: number): void;
    loadSample(sample: LoadSample): void;
    warn(message: string, parameters: object): void;
}

/** One target: its sessions, transport choice, recovery and windows (spec 6). */
class TargetProbe {
    readonly windows: WindowAggregator;
    private readonly pacer: Pacer;
    private channel?: Channel;
    private classifier?: SessionClassifier;
    private transport: Transport = 'udp';
    private sid = 0;
    private seq = 0;
    private open = false;
    private connectAt = 0;
    private gotEcho = false;
    private echoingSince = 0;
    /** Send time of the first probe since the last echo; the time a tab was frozen never counts. */
    private unansweredSince?: number;
    private lastTickAt: number;
    /** The last run of this target's tick or echo handler. */
    private lastActivity: number;
    /** A stall ended since the previous tick: the next tick leaves the probes to the echoes queued behind it. */
    private afterStall = false;
    /** The page was hidden at the previous tick. */
    private wasHidden = false;
    /** Index into RECONNECT_BACKOFF_MS; reset once a session has echoed for STABLE_MS. */
    private attempt = 0;
    /** UDP echoed on this target since it last went `failed`. */
    private udpEstablished = false;
    private silentUdpReconnects = 0;
    /** This UDP session is a trial started from a working TCP session. */
    private trial = false;
    private trialIndex = 0;
    /** The pending recovery step (backoff, UDP trial, retry), fired by the first tick at or after `at`. */
    private due?: { at: number; fn: () => void };
    private stopped = false;

    constructor(
        private readonly ctx: Context,
        readonly name: string,
        readonly url: string,
        startMono: number,
        startWall: number
    ) {
        this.windows = new WindowAggregator(name, startMono, startWall);
        this.pacer = new Pacer(ctx.intervalMs);
        this.lastTickAt = startMono;
        this.lastActivity = startMono;
    }

    connect(transport: Transport, trial = false): void {
        if (this.stopped) return;
        this.due = undefined;
        this.endSession('switch');
        this.transport = transport;
        this.trial = trial;
        this.ctx.status(this.name, { state: 'connecting' });
        const token = this.ctx.token(this.name);
        this.sid = tokenClaims(token).sid;
        this.seq = 0;
        this.gotEcho = false;
        this.open = false;
        this.unansweredSince = undefined;
        this.connectAt = this.ctx.clock.now();
        const ch: Channel = this.ctx.openChannel(
            { url: this.url, token, kind: 'probe', transport },
            {
                onOpen: () => {
                    if (this.channel !== ch) return;
                    this.open = true;
                    this.classifier = new SessionClassifier(transport);
                },
                onMessage: (data) => {
                    if (this.channel === ch) this.onEcho(data);
                },
                onClose: (reason) => {
                    if (this.channel === ch) this.fail(reason);
                }
            }
        );
        this.channel = ch;
    }

    tick(now: number): void {
        this.active(now);
        // The span since the previous tick is background when the page was hidden at either end: a background
        // tab that was frozen and thaws visible must not pass its frozen time off as foreground.
        const hidden = this.ctx.hidden();
        if (hidden || this.wasHidden) this.windows.addBackground(this.lastTickAt, now);
        this.wasHidden = hidden;
        // Recovery runs on the ticks, which come from the worker: main-thread timers are throttled in a background tab.
        if (this.due && now >= this.due.at) {
            const f = this.due.fn;
            this.due = undefined;
            f();
        }
        // A frozen tab cannot tell a dead line: the echoes may sit in the event queue behind this tick.
        if (now - this.lastTickAt > FROZEN_MS) this.rebase(now);
        this.lastTickAt = now;
        if (this.open && this.unansweredSince !== undefined && now - this.unansweredSince > NO_ECHO_MS) this.fail('no_echo');
        else if (this.channel && !this.open && now - this.connectAt > NO_ECHO_MS) this.fail('connect_timeout');
        if (this.open && this.gotEcho && this.attempt > 0 && now - this.echoingSince >= STABLE_MS) this.attempt = 0;
        const ch = this.channel;
        const c = this.classifier;
        const live = ch !== undefined && this.open;
        const busy = live && ch.bufferedAmount() > 0;
        const { spans, send } = this.pacer.tick(now, { hasSession: live, busy });
        for (const s of spans) this.windows.addCoverage(s.state, s.fromMono, s.toMono);
        if (send && ch && c) {
            const clientSendUs = Math.round(now * 1000);
            this.unansweredSince ??= now;
            ch.send(encodeProbe({ sessionId: this.sid, seq: this.seq, clientSendUs }));
            // The send can end the session synchronously; the next session then owns seq.
            if (this.channel === ch) {
                c.sent(this.seq, now, clientSendUs);
                this.seq++;
            }
        }
        // The first tick after a stall can run before the echoes queued behind it: resolve (and seal) on the next one.
        if (this.afterStall) {
            this.afterStall = false;
            return;
        }
        if (this.classifier) this.take(this.classifier.resolve(now));
        for (const s of this.windows.seal(now)) this.ctx.summary(s);
    }

    stop(): void {
        if (this.stopped) return;
        this.stopped = true;
        this.due = undefined;
        this.endSession('stopped');
        for (const s of this.windows.flush(this.ctx.clock.now())) this.ctx.summary(s);
        this.ctx.status(this.name, { state: 'stopped' });
    }

    private onEcho(data: ArrayBuffer): void {
        this.active(this.ctx.clock.now());
        const e = decodeEcho(data);
        if (!e || e.sessionId !== this.sid || !this.classifier) return;
        const now = this.ctx.clock.now();
        // An echo queued behind a frozen tab can run before this target's first tick of the thaw.
        if (now - this.lastTickAt > FROZEN_MS) this.rebase(now);
        this.classifier.echo(e, now);
        this.windows.echoArrived(now);
        this.unansweredSince = undefined;
        if (this.gotEcho) return;
        this.gotEcho = true;
        this.echoingSince = now;
        if (this.transport === 'udp') {
            this.udpEstablished = true;
            this.silentUdpReconnects = 0;
            this.trial = false;
            this.trialIndex = 0;
            this.ctx.status(this.name, { state: 'measuring' });
        } else {
            this.ctx.status(this.name, { state: 'tcp' });
            this.schedule(UDP_RETRY_MS[this.trialIndex], () => this.connect('udp', true));
        }
    }

    private fail(reason: string): void {
        if (!this.channel) return;
        const transport = this.transport;
        const echoed = this.gotEcho;
        this.endSession(reason);
        this.due = undefined;
        if (reason === 'token') this.ctx.requestRenew();
        if (transport === 'udp' && this.trial && !echoed) {
            // TCP worked a moment ago: back to it at once, and try UDP less often.
            this.trialIndex = Math.min(this.trialIndex + 1, UDP_RETRY_MS.length - 1);
            return this.connect('tcp');
        }
        if (WAIT_REASONS.has(reason)) return this.failed(reason);
        if (transport === 'udp') {
            if (echoed) return this.schedule(this.backoff(), () => this.connect('udp'));
            if (this.udpEstablished && ++this.silentUdpReconnects < SILENT_UDP_RECONNECTS) return this.schedule(this.backoff(), () => this.connect('udp'));
            this.silentUdpReconnects = 0;
            return this.connect('tcp');
        }
        if (echoed) return this.schedule(this.backoff(), () => this.connect('tcp'));
        this.failed(reason);
    }

    private failed(reason: string): void {
        this.udpEstablished = false;
        this.silentUdpReconnects = 0;
        this.ctx.status(this.name, { state: 'failed', reason });
        this.schedule(RETRY_MS, () => this.connect('udp'));
    }

    /** Ends the current session, if any, and resolves its probes. */
    private endSession(reason: string): void {
        const ch = this.channel;
        if (!ch) return;
        this.channel = undefined;
        ch.close();
        const now = this.ctx.clock.now();
        let inconsistent = 0;
        const planned = reason === 'switch' || reason === 'stopped';
        if (this.classifier) {
            // A planned end drops the probes still in flight: they were not lost.
            this.take(this.classifier.resolve(now, planned ? 'drop' : 'final'));
            inconsistent = this.classifier.inconsistent;
        }
        this.windows.sessionEnded(now);
        this.classifier = undefined;
        this.open = false;
        // A planned end is not worth a warning, unless the receive counts contradicted the per-seq facts (spec 4.3).
        if (planned) {
            if (inconsistent > 0) this.ctx.warn('Line probe session closed', { target: this.name, transport: this.transport, reason, inconsistent });
        } else {
            this.ctx.warn('Line probe session closed', { target: this.name, transport: this.transport, reason, ...(inconsistent > 0 && { inconsistent }) });
        }
    }

    private take({ probes, pairs }: { probes: ResolvedProbe[]; pairs: PairSample[] }): void {
        if (probes.length === 0 && pairs.length === 0) return;
        this.windows.addProbes(probes, this.ctx.intervalMs);
        this.windows.addPairs(pairs);
        this.ctx.resolved(this.name, probes, pairs);
    }

    /** Records a run of a handler; a gap since the previous run is a main-thread stall, whose probes are not judged. */
    private active(now: number): void {
        if (now - this.lastActivity > STALL_GAP_MS) {
            this.classifier?.stall(this.lastActivity, now);
            this.ctx.stalled(this.name, this.lastActivity, now);
            this.afterStall = true;
        }
        this.lastActivity = now;
    }

    /** Restarts the no-echo timer and the window's silence clock: the time since says nothing about the line. */
    private rebase(now: number): void {
        if (this.unansweredSince !== undefined) this.unansweredSince = now;
        this.windows.restartSilence(now);
    }

    private backoff(): number {
        return RECONNECT_BACKOFF_MS[Math.min(this.attempt++, RECONNECT_BACKOFF_MS.length - 1)];
    }

    private schedule(ms: number, fn: () => void): void {
        this.due = { at: this.ctx.clock.now() + ms, fn };
    }
}

/** The upload load of spec 4.6, on the first target only. Every target's windows record its level while it runs. */
class LoadControl {
    private channel?: Channel;
    private sender?: LoadSender;
    private level: LoadLevel = 'off';
    private kbps = 0;
    private sid = 0;
    private sentSinceReport = 0;
    /** When the load reopens after it closed; checked on the ticks, like the targets' recovery. */
    private retryAt?: number;
    /** The pending retry waits out a server refusal; a level change must not open early. */
    private waiting = false;

    constructor(
        private readonly ctx: Context,
        private readonly target: TargetProbe,
        private readonly onStatus: (status: LoadStatus) => void,
        private readonly record: (level: LoadLevel, kbps: number) => void
    ) {}

    set(level: LoadLevel, kbps: number): void {
        this.level = level;
        this.kbps = kbps;
        if (level === 'off') {
            this.close();
            this.onStatus({ state: 'off' });
        } else if (this.sender) {
            this.sender.setRate(kbps);
            this.record(level, kbps);
        } else if (!this.channel && !(this.retryAt !== undefined && this.waiting)) {
            this.open();
        }
    }

    tick(now: number): void {
        if (this.retryAt !== undefined && now >= this.retryAt) {
            this.retryAt = undefined;
            if (this.level !== 'off') this.open();
        }
        this.sender?.tick(now);
    }

    close(): void {
        this.retryAt = undefined;
        if (this.sender) this.record('off', 0);
        const ch = this.channel;
        this.channel = undefined;
        this.sender = undefined;
        ch?.close();
    }

    private open(): void {
        this.close();
        const token = this.ctx.token(this.target.name);
        const claims = tokenClaims(token);
        if (!claims.loadMaxKbps) {
            this.onStatus({ state: 'unavailable', reason: 'not_allowed' });
            return;
        }
        this.sid = claims.sid;
        this.onStatus({ state: 'connecting' });
        const ch: Channel = this.ctx.openChannel(
            { url: this.target.url, token, kind: 'load', transport: 'udp' },
            {
                onOpen: () => {
                    if (this.channel !== ch) return;
                    this.sentSinceReport = 0;
                    this.sender = new LoadSender(ch, (bytes) => {
                        this.sentSinceReport += bytes;
                        this.target.windows.addLoadSent(bytes, this.ctx.clock.now());
                    });
                    this.sender.setRate(this.kbps);
                    this.record(this.level, this.kbps);
                    this.onStatus({ state: 'running' });
                },
                onMessage: (data) => {
                    if (this.channel !== ch) return;
                    const r = decodeLoadReport(data);
                    if (!r || r.sessionId !== this.sid) return;
                    this.target.windows.addLoadReceived(r.bytes, this.ctx.clock.now());
                    this.ctx.loadSample({ atWall: this.ctx.clock.wall(), sentBytes: this.sentSinceReport, receivedBytes: r.bytes });
                    this.sentSinceReport = 0;
                },
                onClose: (reason) => {
                    if (this.channel !== ch) return;
                    this.close();
                    if (reason === 'token') this.ctx.requestRenew();
                    this.onStatus({ state: 'unavailable', reason });
                    this.waiting = WAIT_REASONS.has(reason);
                    this.retryAt = this.ctx.clock.now() + RETRY_MS;
                },
                onDrain: () => {
                    if (this.channel === ch) this.sender?.pump();
                }
            }
        );
        this.channel = ch;
    }
}

/**
 * Measures the line towards every target of a grant (spec sections 4 and 6) and hands each sealed
 * 10-second window to `onSummary`. Never throws once started; failures become statuses.
 */
export class LineProbeRunner {
    private readonly clock: Clock;
    private readonly ctx: Context;
    private grant?: SessionGrant;
    private targets: TargetProbe[] = [];
    private load?: LoadControl;
    private ticker?: Ticker;
    private tickIndex = 0;
    private renewTimer?: unknown;
    private renewing = false;
    private stopped = false;
    /** The latest setLoad, applied at the end of start() when it came earlier. */
    private requestedLoad: { level: Exclude<LoadLevel, 'off'>; kbps: number } | null = null;

    constructor(private readonly options: RunnerOptions) {
        this.clock = options.clock ?? browserClock;
        const warn = options.warn ?? ((message: string, parameters?: object) => logWarning(message, parameters));
        this.ctx = {
            clock: this.clock,
            openChannel: options.openChannel ?? openBrowserChannel,
            intervalMs: options.intervalMs,
            token: (name) => this.grant?.targets.find((t) => t.name === name)?.token ?? '',
            requestRenew: () => this.renew(),
            hidden: options.isHidden ?? (() => typeof document !== 'undefined' && document.visibilityState === 'hidden'),
            status: (target, status) => options.onStatus?.(target, status),
            summary: (summary) => options.onSummary(summary),
            resolved: (target, probes, pairs) => options.onResolved?.(target, probes, pairs),
            stalled: (target, fromMono, toMono) => options.onStalled?.(target, fromMono, toMono),
            loadSample: (sample) => options.onLoadSample?.(sample),
            warn: (message, parameters) => warn(message, parameters)
        };
    }

    get sessionId(): string | undefined {
        return this.grant?.sessionId;
    }

    async start(): Promise<void> {
        this.grant = await this.options.getGrant();
        if (this.stopped || this.grant.targets.length === 0) return;
        const startMono = this.clock.now();
        const startWall = this.clock.wall();
        this.targets = this.grant.targets.map((t) => new TargetProbe(this.ctx, t.name, t.url, startMono, startWall));
        this.load = new LoadControl(
            this.ctx,
            this.targets[0],
            (s) => this.options.onLoadStatus?.(s),
            (level, kbps) => {
                const now = this.clock.now();
                for (const t of this.targets) t.windows.setLoadLevel(level, kbps, now);
            }
        );
        for (const t of this.targets) t.connect('udp');
        const createTicker = this.options.createTicker ?? createWorkerTicker;
        this.ticker = createTicker(this.options.intervalMs / this.targets.length, () => this.onTick());
        this.scheduleRenew(RENEW_AFTER_MS);
        if (this.requestedLoad) this.setLoad(this.requestedLoad);
    }

    setLoad(load: { level: Exclude<LoadLevel, 'off'>; kbps: number } | null): void {
        if (this.stopped) return;
        this.requestedLoad = load;
        if (this.targets.length === 0) return;
        this.load?.set(load?.level ?? 'off', load?.kbps ?? 0);
    }

    stop(): void {
        if (this.stopped) return;
        this.stopped = true;
        this.ticker?.stop();
        this.load?.close();
        if (this.renewTimer !== undefined) this.clock.clearTimeout(this.renewTimer);
        for (const t of this.targets) t.stop();
    }

    private onTick(): void {
        if (this.stopped || this.targets.length === 0) return;
        const now = this.clock.now();
        this.targets[this.tickIndex++ % this.targets.length].tick(now);
        this.load?.tick(now);
    }

    private scheduleRenew(ms: number): void {
        this.renewTimer = this.clock.setTimeout(() => {
            this.renewTimer = undefined;
            this.renew();
        }, ms);
    }

    /** Fetches a fresh grant; running sessions keep their token until the server closes them. */
    private renew(): void {
        if (this.renewing || this.stopped) return;
        this.renewing = true;
        if (this.renewTimer !== undefined) {
            this.clock.clearTimeout(this.renewTimer);
            this.renewTimer = undefined;
        }
        this.options.getGrant().then(
            (grant) => {
                if (this.stopped) return;
                this.grant = grant;
                this.renewing = false;
                this.scheduleRenew(RENEW_AFTER_MS);
            },
            () => {
                if (this.stopped) return;
                this.renewing = false;
                this.scheduleRenew(RETRY_MS);
                this.ctx.warn('Line probe grant renewal failed', {});
            }
        );
    }
}
